import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import ErrorIcon from 'app/components/Icons/ErrorIcon';
import SuccessIcon from 'app/components/Icons/SuccessIcon';
import { dialogContent, paperProps } from 'app/components/SuccessModal/SuccessModal.sx';

export interface ResultDialogAction {
  label: string;
  onClick: () => void;
  color?: 'primary' | 'secondary';
}

export interface ResultDialogProps {
  open: boolean;
  variant: 'success' | 'error';
  title: string;
  /** One action renders a full-width button, several render a centered row. */
  actions: ResultDialogAction[];
}

const iconSx = { width: '64px', height: '64px' };

/** Presentational result dialog (icon + title + actions). Holds no routing logic. */
const ResultDialog = ({ open, variant, title, actions }: ResultDialogProps) => {
  const renderButton = (action: ResultDialogAction, fullWidth: boolean) => (
    <Button
      key={action.label}
      color={action.color ?? 'secondary'}
      sx={fullWidth ? { width: '100%' } : undefined}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );

  return (
    <Dialog
      open={open}
      PaperProps={{ sx: paperProps }}
      // Backdrop click / Escape must not dismiss a result dialog; it closes only via its actions.
      onClose={() => undefined}
    >
      <DialogContent sx={dialogContent}>
        {variant === 'success' ? <SuccessIcon sx={iconSx} /> : <ErrorIcon sx={iconSx} />}
        <Typography sx={{ fontSize: '26px', fontWeight: 600, textAlign: 'center' }}>
          {title}
        </Typography>
        {actions.length === 1 ? (
          renderButton(actions[0], true)
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            {actions.map(action => renderButton(action, false))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResultDialog;
